import { CircularProgress, makeStyles } from '@material-ui/core';
import { capitalizeAll, TimeSeriesChartPanelProps, TimeSeriesType, TimeSpan } from '@moderepo/mode-app-components';
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { RouteHelper } from '../../routes/RouteHelper';
import { KPIMetricsComparisonViewContainer } from './KPIMetricsComparisonViewContainer';
import { createFetchEntityMetricDataParamsGroup, getEntityMetricGroupPreviewData, getMetricsDefinitionMetricsId, getKPIMetricsFromMetricDefinition,
    MetricPreviewData } from '../utils';
import { ReadonlyNonEmptyArray } from '../../../utils/utils';
import { filterMetricsByMetricsExclusionCustomSettings, GridSizeSettings, MetricsExclusionCustomSettings, useSelectTranslatedEntityClasses }
    from '..';
import { BasicXYChart } from '../../../utils/constants';


const useStyle = makeStyles(() => {
    return {
        root: {
        },

        comparisonChart: {
        },
    };
}, { name: 'KPIMetricDefinitionViewContainer', index: 1 });



export interface KPIMetricDefinitionViewContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;
    readonly timeSpan: TimeSpan;
    readonly timestamps: ReadonlyNonEmptyArray<number>;
    readonly metricDefinitionId: string;

    // To include/exclude metrics we want or don't want to show
    readonly metricsExclusionSettings?: MetricsExclusionCustomSettings | undefined;

    // To specify a different chart type to use for ALL metrics shown in this component instead of using the default chart type, AREA.
    readonly chartType?: BasicXYChart | undefined;

    // To specify a different chart type for a specific metric/metrics. This allow the user to choose a different chart type
    // for any metric instead of using the default chart type, AREA. Note that this is a map of metricName => chartType instead
    // 1 attribute because sometime we want to use a different chart type just for 1 or some metrics but not all metrics.
    readonly chartTypePerMetric?: {
        readonly [metricName: string]: BasicXYChart | undefined;
    } | undefined;

    readonly gridSizes?: GridSizeSettings | undefined;
}



/**
 * This component is used for allowing the user to compare data of the same metric but in different time interval.
 *
 * This component display all numeric metrics for 1 entity's metricDefinition. Each metric will be displayed in a separate chart.
 * Each chart will display multiple series and each series show data for the same metric but different time interval.
 * For example, 1 chart can show many series for Temperature data. 1 series can show temperature data for this month, another series
 * show temperature data for the previous month, and another series show data for the month before that.
 */
export const KPIMetricDefinitionViewContainer: React.FC<KPIMetricDefinitionViewContainerProps> = (
    props: KPIMetricDefinitionViewContainerProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const navigate = useNavigate();
    const routeHelper = RouteHelper.getInstance(navigate);


    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    // The selectedEntityClass has multiple metricDefinitions. We need to get the single metricDefinition specified by props.metricDefinitionId
    const selectedMetricDefinition = useMemo(() => {
        if (selectedEntityClass) {
            return selectedEntityClass.metricsDefinitions?.find((metricDef) => {
                return metricDef.metricsDefinitionId === props.metricDefinitionId;
            });
        }
        return undefined;
    }, [props.metricDefinitionId, selectedEntityClass]);


    /**
     * Go through the metricDefinition's numericMetrics array and get the list of KPI numeric metrics, the numeric metrics that has 'evaluator'
     * attribute.
     */
    const kpiNumericMetrics = useMemo(() => {
        if (selectedMetricDefinition) {
            // Get all the KPI metrics for the metric definition
            const kpiMetrics = getKPIMetricsFromMetricDefinition(selectedMetricDefinition);

            // If "metricsExclusionSettings" is provided, filter out metrics based on "metricsExclusionSettings"
            const filteredKpiMetrics = filterMetricsByMetricsExclusionCustomSettings(
                selectedMetricDefinition.metricsDefinitionId, kpiMetrics, props.metricsExclusionSettings, true,
            );
            return filteredKpiMetrics;
        }
        return undefined;
    }, [props.metricsExclusionSettings, selectedMetricDefinition]);



    /**
     * Fetch and cache data for ALL KPI metrics for the selected entity. We will fetch data in this container and pass it to the children.
     * This way we can optimize and fetch data for multiple metrics in 1 call. If each child are in charge of fetching data for itself, then
     * there will be 1 API call for each metric.
     * metricsData will be an ARRAY of data for multiple timestamps. Each item in the array is for 1 timestamp. The item will be the data
     * for each metric.
     */
    const [multiIntervalMetricsData, setMultiIntervalMetricsData] = useState<MetricPreviewData[][] | undefined>();

    const fetchData = useCallback(async () => {
        if (selectedMetricDefinition && selectedEntity) {
            const timeSpan = props.timeSpan;
            const timestamps = props.timestamps;

            // Call 'createFetchEntityMetricDataParams' to create the information we need to fetch metric data. This DOES NOT fetch data,
            // it only return array of objects which contains information we need for making API calls later.
            const multipleFetchDataParamsGroups = createFetchEntityMetricDataParamsGroup(
                [selectedMetricDefinition], selectedEntity, true, props.metricsExclusionSettings,
            );

            // Compile a list of getEntityKPIMetricGroupPreviewData requests
            const requests: Promise<MetricPreviewData[]>[] = [];

            timestamps.forEach((timestamp, index) => {
                multipleFetchDataParamsGroups.forEach((group) => {
                    // Call getEntityKPIMetricGroupPreviewData to fetch data for group of KPI metrics. For 'currentValue', we only need to
                    // fetch currentValue for the FIRST timestamp therefore we will pass 'false' for the 'includeCurrentValue' option
                    requests.push(getEntityMetricGroupPreviewData(group, timestamp, timeSpan, index === 0, true));
                });
            });

            // Resolve all the requests in 1 promise.
            const allResponses = await Promise.allSettled(requests);

            /**
             * Once we have the responses, we have to regroup the responses based on the number of timestamp. The reason is when we compile a
             * list of fetch data actions, we have flatten them into 1D array while they should be 2D array. For example: Let say we
             * have 3 timestamps and 3 metrics to fetch data for but the 3 metrics have to be split into 2 API calls because they have different
             * aggregation. Therefore these will be the fetch data request we will make
             *
             *      Today
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *      Yesterday
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *      The day before yesterday
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *
             * But because we flatten the fetch calls, they will be like this
             *      requests = [
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *          fetch(temperature, humidity)
             *          fetch(uptime)
             *      ]
             *
             * The 'requests' array will be an array of 6 API calls so therefore we will get 6 responses. However, we can't just leave the
             * responses as flat array because we won't know which result is for which time interval. So we need to group the responses into
             * group based on the timestamps. We will need to group them like this
             *      Today
             *          [responses1, responses2]
             *      Yesterday
             *          [responses1, responses2]
             *      The day before yesterday
             *          [responses1, responses2]
             */

            const resultsGroupedByTimestamp: MetricPreviewData[][] | undefined = (new Array(timestamps.length)).fill([]);

            allResponses.forEach((singleResult, index) => {
                const timestampIndex = Math.floor(index / multipleFetchDataParamsGroups.length);
                if (singleResult.status === 'fulfilled') {
                    // 'value' is the returned value from the getEntityKPIMetricGroupPreviewData call which will be an array of MetricPreviewData
                    resultsGroupedByTimestamp[timestampIndex] = [
                        ...resultsGroupedByTimestamp[timestampIndex],
                        ...singleResult.value,
                    ];
                }
            });

            return resultsGroupedByTimestamp;
        }
        return undefined;
    }, [props.metricsExclusionSettings, props.timeSpan, props.timestamps, selectedEntity, selectedMetricDefinition]);

    useEffect(() => {
        fetchData().then((data) => {
            setMultiIntervalMetricsData(data);
        });
    }, [fetchData]);



    /**
     * On view historical data button clicked, take the user to the historical data page
     */
    const onViewHistoricalData = useCallback(() => {
        if (selectedEntity && selectedMetricDefinition) {
            // Get the collectionId for the given metricDefinitionId
            const collectionId = getMetricsDefinitionMetricsId(selectedEntity, props.metricDefinitionId);

            routeHelper.gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                homeId           : selectedEntity.homeId,
                tsdbSmartModuleId: selectedMetricDefinition.dataStoreConfiguration.moduleInstanceId,
                dataId           : collectionId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
                metricsDefinition: {
                    entityClass        : selectedEntity.entityClass,
                    metricsDefinitionId: selectedMetricDefinition.metricsDefinitionId,
                },
            });
        }
    }, [props.metricDefinitionId, props.projectId, routeHelper, selectedEntity, selectedMetricDefinition]);


    /**
     * Create a set of action to show in each chart panel.
     */
    const chartPanelActions = useMemo((): TimeSeriesChartPanelProps['customSecondaryActions'] => {
        return [{
            icon   : 'show_chart',
            label  : capitalizeAll(trans('label.view_historical_data')),
            onClick: onViewHistoricalData,
        }];
    }, [trans, onViewHistoricalData]);



    return (
        <>
            {project && selectedEntity && selectedMetricDefinition && kpiNumericMetrics && (
                <>
                    {kpiNumericMetrics.map((metric, index) => {
                        return (
                            <KPIMetricsComparisonViewContainer
                                key={metric.name}
                                className={classes.comparisonChart}
                                projectId={props.projectId}
                                entityId={props.entityId}
                                metricDefinition={selectedMetricDefinition}
                                chartIdPrefix={`
                                    EntityKPIMetricsViewContainer-${props.entityId}-${selectedMetricDefinition.metricsDefinitionId}-${index}
                                `}
                                metric={metric}
                                aggregation={metric.evaluator?.aggregation}
                                timestamps={props.timestamps}
                                timeSpan={props.timeSpan}
                                customSecondaryActions={chartPanelActions}
                                multiIntervalMetricsData={multiIntervalMetricsData}
                                chartType={props.chartTypePerMetric?.[metric.name] ?? props.chartType}
                                gridSizes={props.gridSizes}
                            />
                        );
                    })}
                </>
            )}

            {!(project && selectedEntity && selectedMetricDefinition && kpiNumericMetrics) && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityKPIMetricsViewContainer', KPIMetricDefinitionViewContainer);
